import React, { useContext } from 'react';
import { Card, CardContent, CardHeader, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { useEisRuleDetailsContext } from '../../context';
import ActionMenu from 'components/_atoms/ActionMenu';
import DetailList from '_lex/_atoms/DetailList';
import PhxBadge from '_lex/_atoms/PhxBadge';

export default function RuleDetails() {
  const navigate = useNavigate();
  const { hasPermission, currentOrganizationId } = useContext(AppContext);
  const { rule, isLoading } = useEisRuleDetailsContext();
  const canManageFieldBank = hasPermission('manageFieldBank', currentOrganizationId || '');

  const formatTemplateSource = () => {
    if (!rule?.template) {
      return '--';
    }
    return rule.template.name;
  };

  const formatThreshold = () => {
    if (
      !rule?.activeVersion ||
      rule.activeVersion.thresholdPoints === null ||
      rule.activeVersion.thresholdPoints === undefined
    ) {
      return '--';
    }
    return `${rule.activeVersion.thresholdPoints} pts`;
  };

  const formatTimeWindow = () => {
    if (!rule?.activeVersion || !rule.activeVersion.windowType) {
      return '--';
    }
    if (rule.activeVersion.windowType === 'YearToDate') {
      return t('performanceReporting.eis.list.windowType.YTD');
    }
    if (rule.activeVersion.windowDays == null) {
      return '--';
    }
    return `${rule.activeVersion.windowDays} ${t('performanceReporting.eis.list.windowType.RollingDays')}`;
  };

  const formatFlags = () => {
    if (rule?.versionStatus === 'Draft') {
      return '--';
    }
    if (rule?.flagsCount === undefined || rule.flagsCount === null) {
      return '--';
    }
    return rule.flagsCount.toString();
  };

  const formatStatus = () => {
    if (!rule?.versionStatus) {
      return '--';
    }
    const status = rule.versionStatus;
    const statusKey = status.toUpperCase();
    let color: 'success' | 'error' | 'default' = 'default';
    if (status === 'Active') color = 'success';
    else if (status === 'Draft') color = 'default';
    else if (status === 'Inactive') color = 'error';

    return (
      <PhxBadge
        color={color}
        size='sm'
      >
        {t(`performanceReporting.eis.list.status.${statusKey}`) || status}
      </PhxBadge>
    );
  };

  const ruleDetailsItems = [
    {
      label: t('performanceReporting.eis.details.fields.ruleName'),
      value: rule?.name || '--',
    },
    {
      label: t('performanceReporting.eis.details.fields.templateSource'),
      value: formatTemplateSource(),
    },
    {
      label: t('performanceReporting.eis.details.fields.description'),
      value: rule?.description || '--',
    },
    {
      label: t('performanceReporting.eis.details.fields.groups'),
      value: '--',
    },
    {
      label: t('performanceReporting.eis.details.fields.threshold'),
      value: formatThreshold(),
    },
    {
      label: t('performanceReporting.eis.details.fields.timeWindow'),
      value: formatTimeWindow(),
    },
    {
      label: t('performanceReporting.eis.details.fields.status'),
      value: formatStatus(),
    },
    {
      label: t('performanceReporting.eis.details.fields.flags'),
      value: formatFlags(),
    },
  ];

  const actionMenuItems = React.useMemo(() => {
    if (!canManageFieldBank || !rule) return [];
    return [
      {
        key: 'edit-details',
        label: t('performanceReporting.eis.details.actions.editDetails'),
        onClick: () => navigate('edit'),
      },
      {
        key: 'configure-rule',
        label: t('performanceReporting.eis.details.actions.configureRule'),
        onClick: () => navigate(`/admin/performance-reporting/early-intervention-system/configuration/${rule.id}`),
      },
    ];
  }, [canManageFieldBank, rule]);

  return (
    <Stack spacing={12}>
      <Card className='lex-card'>
        <CardHeader
          title={t('performanceReporting.eis.details.sections.ruleDetails')}
          action={
            canManageFieldBank ? (
              <ActionMenu
                id='eis-rule-details-actions'
                btnText={t('performanceReporting.eis.details.actions.edit')}
                btnProps={{ variant: 'contained', size: 'small', disabled: isLoading }}
                items={actionMenuItems}
              />
            ) : null
          }
        />
        <CardContent>
          <DetailList items={ruleDetailsItems} />
        </CardContent>
      </Card>
    </Stack>
  );
}
